import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { ActionButton, IButtonStyles, IconButton, mergeStyleSets, Stack, StackItem } from '@fluentui/react'
import FileHeader from '../Render/FileHeader'
import { generatePath } from 'react-router'
import { fileRoutePrefix } from '../../hooks/file/useFilePages'
import { isAppContext } from '../../helpers/appHelper'
import { AppContext } from '../../config/appConfig'
import ComposeEmailDialog, { ComposeEmailDialogProps } from '../Dialog/OutlookItem/ComposeEmailDialog'
import ComposeAppointmentDialog, { ComposeAppointmentDialogProps } from '../Dialog/OutlookItem/ComposeAppointmentDialog'
import { FileAction } from '../../config/fileConfig'
import Card from '../Card'
import { ViewFileProps } from '../../pages/File/ViewFile'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { isOfficeInitialized } from '../../helpers/outlookHelper'
import DialogButton from '../Button/DialogButton'
import { DocumentGenerationParams, DocumentGenerationType } from '../../hooks/document/useDocumentGenerationHandler'
import useSubmitDocumentGenerationRequest from '../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { ModuleType } from '../../context/GlobalContext'
import useModules from '../../hooks/module/useModules'
import useRoles, { Role, rolesTasks, rolesTimes } from '../../hooks/useRoles'
import { useTranslation } from 'react-i18next'
import { SharePointIcon } from '../Icon/SharePointIcon'
import { Button } from '@fluentui/react-components'
import useSharePoint from '../../hooks/useSharePoint'
import { IGraphResponse } from '../../context/AxiosContext'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { DRIVE_ITEM_CONFIG, getFullPath } from '../TextField/Folder/FolderField'
import useAxios from '../../hooks/useAxios'

type FileCardParamsDto = DocumentGenerationParams<{
    fileId: string
}>

export default function ViewFileHeader({ file, fileId }: ViewFileProps): ReactElement {
    const axios = useAxios()
    const dispatch = useDispatch()
    const history = useHistory()
    const roles = useRoles()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const { isViewport, viewport } = useViewport()

    const routeMatch = useRouteMatch<{ fileId: string }>()
    const { sharePoint } = useSharePoint()
    const submitRequest = useSubmitDocumentGenerationRequest<FileCardParamsDto>(DocumentGenerationType.FileCard)
    const actionButtonStyles: IButtonStyles = useMemo(
        () =>
            mergeStyleSets({
                root: {
                    height: 'inherit',
                },
                flexContainer: {
                    flexDirection: isViewport('>=', Viewport.OutlookNewWide) ? 'row' : 'column',
                    gap: isViewport('>=', Viewport.OutlookNewWide) ? 0 : '4px',
                },
            }),
        [viewport],
    )

    const [currentDocumentRootUrl, setCurrentDocumentRootUrl] = useState<string>()
    useEffect(() => {
        if (sharePoint?.list?.webUrl && file.current.data.documentRootId) {
            axios.get(`/items/${file.current.data.documentRootId}`, DRIVE_ITEM_CONFIG).then((response: IGraphResponse<DriveItem>) => {
                if (response.data) {
                    setCurrentDocumentRootUrl(sharePoint?.list?.webUrl + '/' + encodeURIComponent(getFullPath(response.data)))
                }
            })
        }
    }, [sharePoint])

    return (
        <Card>
            <Stack horizontal>
                <Stack grow tokens={{ childrenGap: 0 }}>
                    <Stack tokens={{ padding: 's1' }}>
                        <FileHeader file={file.current.data} showTitle />
                    </Stack>
                    <StackItem grow>
                        <span />
                    </StackItem>
                    <Stack horizontal>
                        {modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes) && (
                            <ActionButton
                                text={isViewport('>', Viewport.OutlookWeb) && translate('action.create.time.title')}
                                iconProps={{ iconName: 'Timer' }}
                                styles={actionButtonStyles}
                                onClick={() => history.push(generatePath(`${fileRoutePrefix}/time/add`, routeMatch.params))}
                            />
                        )}
                        {modules.any([ModuleType.Tasks, ModuleType.Workflow]) && roles.any(rolesTasks) && (
                            <ActionButton
                                text={isViewport('>', Viewport.OutlookWeb) && translate('action.create.task.title')}
                                iconProps={{ iconName: 'TaskLogo' }}
                                styles={actionButtonStyles}
                                onClick={() =>
                                    history.push(
                                        generatePath(
                                            `${fileRoutePrefix}/${modules.has(ModuleType.Workflow) ? 'workflow' : 'task'}/add`,
                                            routeMatch.params,
                                        ),
                                    )
                                }
                            />
                        )}
                        {/*Show this button both versions but not when already composing an e-mail.*/}
                        {!isAppContext(AppContext.Compose) && (
                            <DialogButton<ComposeEmailDialogProps>
                                button={ActionButton}
                                buttonProps={{
                                    text: isViewport('>', Viewport.OutlookWeb) && translate('action.create.email'),
                                    iconProps: { iconName: 'OutlookLogo' },
                                    styles: actionButtonStyles,
                                }}
                                dialog={ComposeEmailDialog}
                                dialogProps={{ file: file.current.data, parties: file.party.data }}
                            />
                        )}
                        {isOfficeInitialized() && !isAppContext(AppContext.Compose) && (
                            <DialogButton<ComposeAppointmentDialogProps>
                                button={ActionButton}
                                buttonProps={{
                                    text: isViewport('>', Viewport.OutlookWeb) && translate('action.create.appointment'),
                                    iconProps: { iconName: 'Calendar' },
                                    styles: actionButtonStyles,
                                }}
                                dialog={ComposeAppointmentDialog}
                                dialogProps={{ file: file.current.data, parties: file.party.data }}
                            />
                        )}
                        {modules.has(ModuleType.Invoices) && roles.any([Role.Invoice]) && (
                            <ActionButton
                                text={isViewport('>', Viewport.OutlookWeb) && translate('action.create.invoice.title')}
                                iconProps={{ iconName: 'Financial' }}
                                styles={actionButtonStyles}
                                onClick={() => history.push(`/invoice/add`, { fileIds: [fileId] })}
                            />
                        )}
                    </Stack>
                </Stack>
                <Stack tokens={{ childrenGap: 0 }}>
                    <IconButton
                        title={translate('file.refresh')}
                        iconProps={{ iconName: 'Refresh' }}
                        onClick={() => dispatch({ type: FileAction.ResetCurrent })}
                    />
                    {roles.none([Role.FileLimited]) && (
                        <IconButton
                            title={translate('file.edit.action')}
                            iconProps={{ iconName: 'Edit' }}
                            onClick={() => history.push(`/file/${fileId}/edit`)}
                        />
                    )}
                    {roles.none([Role.FileLimited]) && (
                        <IconButton
                            title={translate('file.pdf')}
                            iconProps={{ iconName: 'Print' }}
                            onClick={() => {
                                submitRequest({ fileId })
                            }}
                        />
                    )}
                    {file.current.data.documentRootId && (
                        <Button
                            appearance="transparent"
                            value={'sharepoint'}
                            icon={<SharePointIcon />}
                            onClick={() => window.open(currentDocumentRootUrl, '_blank')}
                        />
                    )}
                </Stack>
            </Stack>
        </Card>
    )
}
